// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { Form } from "react-bootstrap";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { makeSortHeader } from "guiutils";
import { ITableSort } from "states/guistates";
import { Site } from "states/sitestates";

import SearchFilter from "view/comp/SearchFilter";
import SelectAllCheck from "view/comp/SelectAllCheck";

const messages = defineMessages({
  searchHint: {
    id: "Type to filter sites...",
  },
});

type SiteSelectionChange = (siteIds: number[], isSelected: boolean) => void;

interface ISiteRowProps {
  checkId: string;
  site: Site;
  isSelected: boolean;
  isReadOnly: boolean;
  onChange: SiteSelectionChange;
}

// memoized, so toggling a site only re-renders its own row
const SiteRow = React.memo(function SiteRow(props: ISiteRowProps) {
  return (
    <tr>
      <td>
        <Form.Label htmlFor={props.checkId} className="link">
          {props.site.name}
        </Form.Label>
      </td>
      <td className="center checkbox">
        <Form.Check
          id={props.checkId}
          checked={props.isSelected}
          disabled={props.isReadOnly}
          onChange={(event) => props.onChange([props.site.id], event.currentTarget.checked)}
        />
      </td>
    </tr>
  );
});

interface ISiteSelectionTableProps {
  // prefix of the checkbox ids
  id: string;
  sites: Site[];
  selectedIds: number[];
  isReadOnly?: boolean;
  // reports the changed sites only; keep it stable to avoid re-rendering all rows
  onChange: SiteSelectionChange;
}

// searchable and sortable site list to select sites via checkboxes
export default function SiteSelectionTable(props: ISiteSelectionTableProps) {
  const isReadOnly = props.isReadOnly ?? false;
  const intl = useIntl();
  const [filterText, setFilterText] = React.useState("");
  const [tableSort, setTableSort] = React.useState<ITableSort>({
    column: "name",
    isSortAscending: true,
  });
  // selection used for sorting, only updated on sort clicks, so rows don't jump when toggled
  const [sortSelectedIds, setSortSelectedIds] = React.useState(props.selectedIds);

  const selected = new Set(props.selectedIds);
  const sortSelected = new Set(sortSelectedIds);
  const lowerFilterText = filterText.toLocaleLowerCase();
  const shownSites = props.sites
    .filter((site) => site.name.toLocaleLowerCase().includes(lowerFilterText))
    .sort((a, b) => {
      // selected sites first on ascending sort, name as secondary key
      const sort =
        (tableSort.column === "selected" ?
          Number(sortSelected.has(b.id)) - Number(sortSelected.has(a.id))
        : 0) || a.name.localeCompare(b.name);
      return tableSort.isSortAscending ? sort : -sort;
    });
  const shownSiteIds = shownSites.map((site) => site.id);

  const makeHeader = (column: string, caption: React.JSX.Element, classes?: string[]) =>
    makeSortHeader(
      props.id,
      column,
      caption,
      tableSort,
      (sort) => {
        setTableSort(sort);
        setSortSelectedIds(props.selectedIds);
      },
      classes,
    );

  return (
    <>
      <SearchFilter
        filterText={filterText}
        placeholder={intl.formatMessage(messages.searchHint)}
        onChange={setFilterText}
      />
      <table className="scrolltable">
        <thead>
          <tr>
            {makeHeader("name", <FormattedMessage id="Site Name" />)}
            {makeHeader(
              "selected",
              <>
                {!isReadOnly && (
                  <SelectAllCheck
                    id={`${props.id}_all`}
                    checkedCount={shownSiteIds.filter((siteId) => selected.has(siteId)).length}
                    totalCount={shownSiteIds.length}
                    onChange={(isSelected) => props.onChange(shownSiteIds, isSelected)}
                  />
                )}
                <FormattedMessage id="Site Linked" />
              </>,
              ["center", "checkbox"],
            )}
          </tr>
        </thead>
        <tbody>
          {shownSites.map((site) => (
            <SiteRow
              key={site.id}
              checkId={`${props.id}_${site.id}`}
              site={site}
              isSelected={selected.has(site.id)}
              isReadOnly={isReadOnly}
              onChange={props.onChange}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}
